import React, { useEffect, useMemo, useState } from "react";

import PageLayout from "../../components/PageLayout";
import { canAccessHedgePlanning, getLoginDetails } from "../../utils/storage";
import InsightsHeaderIcon from "../../icons/newicons/insights.svg";
import { InsightsSubNav } from "./InsightsSubNav";
import {
  aggregateBuying,
  buildSampleLoadItems,
  filterItemsByRange,
  itemsForOem,
  periodBounds,
  unitsBought,
} from "./buyingInsightsSample";

import "./insights.css";
import "./buying-insights.css";

function money(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function moneyExact(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function pct(n, digits = 0) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${Number(n).toFixed(digits)}%`;
}

function formatDay(ms) {
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const TABS = [
  { id: "top", label: "Top cats" },
  { id: "price", label: "Price variance" },
  { id: "buyers", label: "Buyers" },
  { id: "patterns", label: "Buying patterns" },
];

const BuyingInsights = () => {
  const loginDetails = getLoginDetails();
  const canView = canAccessHedgePlanning(loginDetails);

  const [period, setPeriod] = useState("90");
  const [tab, setTab] = useState("top");
  const [selectedOem, setSelectedOem] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const allItems = useMemo(() => buildSampleLoadItems(), []);

  const rangeItems = useMemo(() => {
    const { from, to } = periodBounds(period);
    let list = filterItemsByRange(allItems, from, to);
    if (typeFilter) list = list.filter((it) => it.type === typeFilter);
    return list;
  }, [allItems, period, typeFilter]);

  const report = useMemo(() => aggregateBuying(rangeItems), [rangeItems]);

  const selectedRows = useMemo(
    () => (selectedOem ? itemsForOem(rangeItems, selectedOem) : []),
    [rangeItems, selectedOem]
  );

  const selectedCat = report.cats.find((c) => c.oem === selectedOem);

  const maxDayUnits = Math.max(1, ...report.days.map((d) => d.units));
  const priceCats = [...report.cats].sort((a, b) => b.spreadPct - a.spreadPct);

  useEffect(() => {
    if (!canView) window.location.href = "/";
  }, [canView]);

  if (!canView) return null;

  const types = ["", ...new Set(allItems.map((i) => i.type))];

  return (
    <PageLayout icon={InsightsHeaderIcon} heading="Insights" searchPlaceholder="" hideSearchBar>
      <InsightsSubNav />

      <div className="buy-ins-page">
        <div className="buy-ins-demo">
          Sample LoadItem history. Price = <code>adjustedPrice</code>, units ={" "}
          <code>finalUnit</code>. Spend = price × units.
        </div>

        <div className="buy-ins-header">
          <div>
            <h2 className="buy-ins-title">Buying Insights</h2>
            <p className="buy-ins-sub">
              What ABCAT bought, at what price, by whom — and where price or buying pattern
              drifts from the pack.
            </p>
          </div>
        </div>

        <div className="buy-ins-kpis">
          <div className="buy-ins-kpi">
            <div className="buy-ins-kpi-label">Units bought</div>
            <div className="buy-ins-kpi-value">{report.units}</div>
            <div className="buy-ins-kpi-hint">finalUnit</div>
          </div>
          <div className="buy-ins-kpi">
            <div className="buy-ins-kpi-label">Spend</div>
            <div className="buy-ins-kpi-value">{money(report.spend)}</div>
            <div className="buy-ins-kpi-hint">adjustedPrice × units</div>
          </div>
          <div className="buy-ins-kpi">
            <div className="buy-ins-kpi-label">Unique cats</div>
            <div className="buy-ins-kpi-value">{report.uniqueCats}</div>
          </div>
          <div className="buy-ins-kpi">
            <div className="buy-ins-kpi-label">Buyers</div>
            <div className="buy-ins-kpi-value">{report.uniqueBuyers}</div>
          </div>
          <div className="buy-ins-kpi">
            <div className="buy-ins-kpi-label">Avg price spread</div>
            <div className="buy-ins-kpi-value">{report.avgSpread}%</div>
            <div className="buy-ins-kpi-hint">min→max vs avg</div>
          </div>
          <div className="buy-ins-kpi">
            <div className="buy-ins-kpi-label">High variance cats</div>
            <div className="buy-ins-kpi-value">{report.highSpread}</div>
            <div className="buy-ins-kpi-hint">spread ≥ 25%</div>
          </div>
        </div>

        <div className="buy-ins-tabs">
          {[
            ["30", "30 days"],
            ["90", "90 days"],
            ["ytd", "YTD"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`buy-ins-pill${period === id ? " is-active" : ""}`}
              onClick={() => setPeriod(id)}
            >
              {label}
            </button>
          ))}
          <select
            className="buy-ins-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {types.map((t) => (
              <option key={t || "all"} value={t}>
                {t || "All types"}
              </option>
            ))}
          </select>
          <span className="buy-ins-tabs-split" />
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`buy-ins-pill${tab === t.id ? " is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "top" && (
          <div className="buy-ins-split">
            <div className="buy-ins-card">
              <div className="buy-ins-card-title">Top converters by units</div>
              <p className="buy-ins-hint">
                Units from finalUnit. Price range is adjustedPrice min / unit-weighted avg / max.
                Click a row for the band and each buy.
              </p>
              <div className="buy-ins-table-wrap">
                <table className="buy-ins-table">
                  <thead>
                    <tr>
                      <th>OEM</th>
                      <th>Type</th>
                      <th>Units</th>
                      <th>Spend</th>
                      <th>Price range</th>
                      <th>Vs catalog</th>
                      <th>Buyers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.cats.map((c) => (
                      <tr
                        key={c.oem}
                        className={selectedOem === c.oem ? "is-open" : ""}
                        onClick={() => setSelectedOem(c.oem === selectedOem ? "" : c.oem)}
                      >
                        <td>
                          <div className="buy-ins-strong">{c.oem}</div>
                          <div className="buy-ins-muted">{c.manufacturer}</div>
                        </td>
                        <td>{c.type}</td>
                        <td className="buy-ins-mono">{c.units}</td>
                        <td className="buy-ins-mono">{moneyExact(c.spend)}</td>
                        <td className="buy-ins-mono">
                          ${c.minPrice}–${c.maxPrice}
                          <div className="buy-ins-muted">avg ${c.avgPrice}</div>
                        </td>
                        <td className={`buy-ins-mono${c.vsCatalogPct > 8 ? " is-neg" : c.vsCatalogPct < -8 ? " is-pos" : ""}`}>
                          {pct(c.vsCatalogPct, 1)}
                        </td>
                        <td>
                          {c.buyerCount}
                          <div className="buy-ins-muted">
                            {c.topBuyer} {c.topBuyerShare}%
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="buy-ins-card">
              <div className="buy-ins-card-title">
                {selectedCat ? selectedCat.oem : "Select a converter"}
              </div>
              {!selectedCat && (
                <p className="buy-ins-hint">Pick a cat on the left to see its price band and buys.</p>
              )}
              {selectedCat && (
                <>
                  <div className="buy-ins-range">
                    <div className="buy-ins-range-labels">
                      <span>${selectedCat.minPrice}</span>
                      <span>avg ${selectedCat.avgPrice}</span>
                      <span>${selectedCat.maxPrice}</span>
                    </div>
                    <div className="buy-ins-range-bar">
                      <span
                        className="buy-ins-range-avg"
                        style={{
                          left: `${
                            selectedCat.maxPrice === selectedCat.minPrice
                              ? 50
                              : ((selectedCat.avgPrice - selectedCat.minPrice) /
                                  (selectedCat.maxPrice - selectedCat.minPrice)) *
                                100
                          }%`,
                        }}
                      />
                      <span
                        className="buy-ins-range-cat"
                        title="Catalog"
                        style={{
                          left: `${Math.max(
                            0,
                            Math.min(
                              100,
                              selectedCat.maxPrice === selectedCat.minPrice
                                ? 50
                                : ((selectedCat.catalogPrice - selectedCat.minPrice) /
                                    (selectedCat.maxPrice - selectedCat.minPrice)) *
                                  100
                            )
                          )}%`,
                        }}
                      />
                    </div>
                    <div className="buy-ins-hint">
                      Catalog ${selectedCat.catalogPrice} · spread {selectedCat.spreadPct}% ·{" "}
                      {selectedCat.buys} buys
                    </div>
                  </div>
                  <table className="buy-ins-table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Buyer</th>
                        <th>Units</th>
                        <th>adjustedPrice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRows.slice(0, 12).map((it) => (
                        <tr key={it.itemId}>
                          <td>{formatDay(it.createdDate)}</td>
                          <td>{it.buyerName}</td>
                          <td className="buy-ins-mono">{unitsBought(it)}</td>
                          <td className="buy-ins-mono">${it.adjustedPrice}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        )}

        {tab === "price" && (
          <div className="buy-ins-card">
            <div className="buy-ins-card-title">Price variance by converter</div>
            <p className="buy-ins-hint">
              Spread = (max − min) ÷ average adjustedPrice (unit-weighted). Vs catalog compares
              that average to catalogPrice. High spread means the same OEM was bought at
              different adjustedPrices.
            </p>
            <div className="buy-ins-table-wrap">
              <table className="buy-ins-table">
                <thead>
                  <tr>
                    <th>OEM</th>
                    <th>Min / avg / max</th>
                    <th>Spread</th>
                    <th>Std dev</th>
                    <th>Vs catalog</th>
                    <th>Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {priceCats.map((c) => (
                    <tr key={c.oem} onClick={() => { setSelectedOem(c.oem); setTab("top"); }}>
                      <td>
                        <div className="buy-ins-strong">{c.oem}</div>
                        <div className="buy-ins-muted">{c.type} · {c.buys} buys</div>
                      </td>
                      <td className="buy-ins-mono">
                        ${c.minPrice} / ${c.avgPrice} / ${c.maxPrice}
                      </td>
                      <td className={`buy-ins-mono${c.spreadPct >= 25 ? " is-neg" : ""}`}>
                        {c.spreadPct}%
                      </td>
                      <td className="buy-ins-mono">${c.stdPrice}</td>
                      <td className={`buy-ins-mono${c.vsCatalogPct > 8 ? " is-neg" : ""}`}>
                        {pct(c.vsCatalogPct, 1)}
                      </td>
                      <td>
                        {c.spreadPct >= 25 ? (
                          <span className="buy-ins-flag">Wide range</span>
                        ) : c.vsCatalogPct >= 8 ? (
                          <span className="buy-ins-flag is-warn">Above catalog</span>
                        ) : (
                          <span className="buy-ins-muted">In band</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "buyers" && (
          <div className="buy-ins-card">
            <div className="buy-ins-card-title">How buyers are buying</div>
            <p className="buy-ins-hint">
              Mix % = share of a buyer’s units (finalUnit) in their top 5 cats. Vs catalog is
              unit-weighted adjustedPrice ÷ catalogPrice.
            </p>
            <div className="buy-ins-table-wrap">
              <table className="buy-ins-table">
                <thead>
                  <tr>
                    <th>Buyer</th>
                    <th>Units</th>
                    <th>Spend</th>
                    <th>Unique cats</th>
                    <th>Top cat</th>
                    <th>Top-5 mix</th>
                    <th>Vs catalog</th>
                    <th>Pattern</th>
                  </tr>
                </thead>
                <tbody>
                  {report.buyers.map((b) => (
                    <tr key={b.userId}>
                      <td className="buy-ins-strong">{b.name}</td>
                      <td className="buy-ins-mono">{b.units}</td>
                      <td className="buy-ins-mono">{moneyExact(b.spend)}</td>
                      <td className="buy-ins-mono">{b.uniqueCats}</td>
                      <td>{b.topCat}</td>
                      <td className="buy-ins-mono">{b.mixPct}%</td>
                      <td className={`buy-ins-mono${b.avgVsCatPct > 5 ? " is-neg" : b.avgVsCatPct < -5 ? " is-pos" : ""}`}>
                        {pct(b.avgVsCatPct, 1)}
                      </td>
                      <td>{b.pattern}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "patterns" && (
          <div className="buy-ins-pattern-grid">
            <div className="buy-ins-card">
              <div className="buy-ins-card-title">Daily units</div>
              <p className="buy-ins-hint">Volume pattern across the selected period.</p>
              <div className="buy-ins-chart">
                {report.days.map((d) => (
                  <div key={d.date} className="buy-ins-chart-col" title={`${d.date}: ${d.units} units`}>
                    <div className="buy-ins-chart-bar-wrap">
                      <div
                        className="buy-ins-chart-bar"
                        style={{ height: `${Math.max(4, (d.units / maxDayUnits) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="buy-ins-card">
              <div className="buy-ins-card-title">Type mix</div>
              <table className="buy-ins-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Units</th>
                    <th>Spend</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {report.types.map((t) => (
                    <tr key={t.type}>
                      <td>{t.type}</td>
                      <td className="buy-ins-mono">{Math.round(t.units)}</td>
                      <td className="buy-ins-mono">{moneyExact(t.spend)}</td>
                      <td className="buy-ins-mono">
                        {report.units ? Math.round((t.units / report.units) * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="buy-ins-hint" style={{ marginTop: 14 }}>
                Pattern flags use LoadItem <code>adjustedPrice</code> and <code>finalUnit</code>
                only. Later we can add DNB items and facility splits.
              </p>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default BuyingInsights;
